import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';

const CHORD_TIMEOUT_MS = 1000;

const chordRoutes: Record<string, string> = {
  d: '/',
  t: '/tenants',
  o: '/orders',
  s: '/subscriptions',
  c: '/catalog',
  i: '/billing/invoices',
};

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  );
}

/**
 * Registers G-then-X chord navigation shortcuts.
 * G+D -> Dashboard, G+T -> Tenants, G+O -> Orders,
 * G+S -> Subscriptions, G+C -> Catalog, G+I -> Invoices
 */
export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const gPressedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === 'g') {
        gPressedRef.current = true;
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          gPressedRef.current = false;
        }, CHORD_TIMEOUT_MS);
        return;
      }

      if (gPressedRef.current) {
        gPressedRef.current = false;
        clearTimeout(timeoutRef.current);
        const path = chordRoutes[e.key];
        if (path) {
          e.preventDefault();
          navigate({ to: path });
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(timeoutRef.current);
    };
  }, [navigate]);
}
